import { useLocation } from '@reach/router';
import React from 'react';

import image from '@skippay/core/assets/images/selfCare/brain-sad.png';
import BgCircles from '@skippay/core/components/BgCircles';
import Button from '@skippay/core/components/Button';
import Headline from '@skippay/core/components/Headline';
import Image from '@skippay/core/components/Image';
import Text from '@skippay/core/components/Text';
import { FormattedMessage, useIntl } from '@skippay/core/i18n/intl';
import { mergeClasses } from '@skippay/core/utils/classUtils';

interface Props {
	title?: string | JSX.Element;
	subheading?: string | JSX.Element;
	onButtonClick?: () => void;
	buttonLabel?: string | JSX.Element;
	error?: {
		error?: Error;
	};
	statusCode?: string | number;
	className?: string;
}

const ErrorText = ({ children }) => (
	<Text size="paragraphSmall" color="gray-500" align="center">
		{children}
	</Text>
);

const ErrorContent: React.FC<Props> = ({
	title,
	subheading,
	error,
	statusCode,
	onButtonClick,
	buttonLabel,
	className,
}) => {
	const { pathname } = useLocation();
	const intl = useIntl();

	return (
		<div
			className={mergeClasses(
				'flex flex-col items-center justify-center bg-white md:bg-blue-100',
				{
					'dark:bg-d-light-gray-50': process.env.MOB_APP_BUILD,
					'h-svh': !className, // in case no classname is passed from props, we set the height to the full viewport height (this is temporary solution until tailwind-merge is fixed)
				},
				className
			)}
		>
			<BgCircles
				position="fixed"
				width="95%"
				left="2.5%"
				top="2.5%"
				className="z-0 hidden md:block"
			/>
			<div
				className={mergeClasses(
					'z-10 mx-auto flex w-full flex-col items-center gap-10 rounded-3xl bg-white p-8 md:max-w-[600px]',
					{
						'dark:bg-d-light-gray-50': process.env.MOB_APP_BUILD,
					}
				)}
			>
				<div className="flex flex-col items-center gap-8">
					<Image
						src={image}
						alt={intl.formatMessage({
							id: 'screens.runtimeErrorScreen.label.title',
							defaultMessage: 'Ajaj, něco se nepovedlo',
						})}
						width="180"
						height="160"
					/>
					<div className="flex flex-col items-center gap-2">
						<Headline mdLg component="h2" center>
							{title ?? (
								<FormattedMessage
									id="screens.runtimeErrorScreen.label.title"
									defaultMessage="Ajaj, něco se nepovedlo"
								/>
							)}
						</Headline>
						<Text size="paragraph" align="center">
							{subheading ?? (
								<FormattedMessage
									id="screens.runtimeErrorScreen.label.message"
									defaultMessage="Zkuste to prosím znovu za chvilku."
								/>
							)}
						</Text>
					</div>
					{onButtonClick && (
						<Button
							onClick={onButtonClick}
							variant="primary"
							testId="error-retry-button"
							analyticsId="errorRetry"
						>
							{buttonLabel ?? (
								<FormattedMessage
									id="component.error.boundary.button"
									defaultMessage="Zkusit znovu"
								/>
							)}
						</Button>
					)}
				</div>
				<div className="flex flex-col items-center gap-1">
					{error && <ErrorText>{error.error?.message}</ErrorText>}

					{statusCode && (
						<ErrorText>
							<FormattedMessage
								id="component.error.boundary.code"
								defaultMessage="Kód&nbsp;chyby:&nbsp;{statusCode}"
								values={{
									statusCode,
								}}
							/>
						</ErrorText>
					)}

					<ErrorText>
						<FormattedMessage
							id="component.error.boundary.timestamp"
							defaultMessage="Čas&nbsp;chyby:&nbsp;{timestamp}"
							values={{
								timestamp: new Date().toLocaleString('en-US', {
									month: '2-digit',
									day: '2-digit',
									year: 'numeric',
									hour: '2-digit',
									minute: '2-digit',
									second: '2-digit',
									hour12: false,
								}),
							}}
						/>
					</ErrorText>

					{pathname && <ErrorText>{pathname}</ErrorText>}
					{process.env.npm_package_version && (
						<ErrorText>{process.env.npm_package_version}</ErrorText>
					)}
				</div>
			</div>
		</div>
	);
};

export default ErrorContent;
